import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { NotificationBell } from "./NotificationBell";

const BASE_NAV_LINKS = [
  { label: "HOW IT WORKS", to: "/how-it-works" },
  { label: "SESSIONS", to: "/find-a-session" },
  { label: "PHOTOGRAPHERS", to: "/photographers" },
  { label: "ABOUT", to: "/about" },
  { label: "SAFETY", to: "/safety" },
  { label: "FAQ", to: "/faq" },
  { label: "CONTACT", to: "/contact" },
];

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

export const Header = ({ bannerVisible }) => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { settings } = useSettings();
  const navLinks = settings?.stories_page_enabled ? [...BASE_NAV_LINKS, { label: "STORIES", to: "/stories" }] : BASE_NAV_LINKS;

  return (
    <header className={`fixed ${bannerVisible ? "top-11" : "top-0"} left-0 right-0 z-50 bg-midnight-black/90 backdrop-blur-md border-b border-white/10 transition-[top] duration-200`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex-shrink-0" onClick={() => setOpen(false)}>
          <Logo className="h-8 sm:h-9" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${slug(l.label)}`}
              className={({ isActive }) =>
                `text-[11px] font-bold tracking-[0.15em] transition-colors ${
                  isActive ? "text-stranger-orange" : "text-clean-white/80 hover:text-stranger-orange"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <>
              <NotificationBell dark />
              <Link to="/dashboard" data-testid="header-dashboard-link">
                <button className="flex items-center gap-2 bg-stranger-orange text-clean-white rounded-full px-5 py-2.5 font-bold tracking-wide text-[11px] hover:bg-orange-600 transition-colors">
                  <LayoutDashboard className="w-3.5 h-3.5" /> DASHBOARD
                </button>
              </Link>
            </>
          ) : (
            <Link to="/login" data-testid="header-login-link" className="text-[11px] font-bold tracking-[0.15em] text-clean-white border border-white/30 rounded-full px-6 py-2.5 hover:border-stranger-orange hover:text-stranger-orange transition-colors">
              LOG IN
            </Link>
          )}
        </div>

        <button className="lg:hidden text-clean-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-midnight-black border-t border-white/10 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="text-sm font-bold tracking-widest text-clean-white/90"
              data-testid={`mobile-nav-link-${slug(l.label)}`}
            >
              {l.label}
            </Link>
          ))}
          <Link to={user ? "/dashboard" : "/login"} onClick={() => setOpen(false)} data-testid="mobile-login-link">
            <button className="bg-stranger-orange w-full rounded-full font-bold tracking-wide text-xs py-3 text-clean-white">
              {user ? "DASHBOARD" : "LOG IN"}
            </button>
          </Link>
        </div>
      )}
    </header>
  );
};
